<template>
  <v-select
    :value="value"
    :input-id="inputId"
    :options="$tOptions(displayOptions)"
    :filterable="false"
    :placeholder="resolvedPlaceholder"
    :loading="initialLoading"
    :clearable="clearable"
    :class="{ 'vs-sm': size === 'sm' }"
    label="label"
    @search="searchAgents"
    @input="handleInput"
    @open="onDropdownOpen"
  >
    <template #option="option">
      <span class="agent-username">{{ option.username }}</span>
      <span
        v-if="option.prefix && sourceConfig.showPrefix"
        class="agent-prefix"
      >({{ option.prefix }})</span>
      <span
        v-if="option.isSelf"
        class="agent-self-badge"
      >{{ $t('ของฉัน') }}</span>
    </template>
    <template #selected-option="option">
      <span class="agent-username">{{ option.username }}</span>
      <span
        v-if="option.prefix && sourceConfig.showPrefix"
        class="agent-prefix"
      >({{ option.prefix }})</span>
      <span
        v-if="option.isSelf"
        class="agent-self-badge"
      >{{ $t('ของฉัน') }}</span>
    </template>
    <template #list-footer>
      <li
        v-if="loadFailed && displayOptions.length"
        class="text-center text-danger py-2"
        role="alert"
      >{{ $t('Unable to load options') }}</li>
      <li
        v-show="hasMoreAgents"
        ref="loadMoreTrigger"
        class="text-center py-2"
        style="cursor: pointer;"
        @click="loadMoreAgents"
      >
        <span v-if="loadingMore">{{ $t('กำลังโหลด...') }}</span>
        <span v-else>{{ $t('โหลดเพิ่มเติม') }}</span>
      </li>
    </template>
    <template #no-options>
      <span v-if="initialLoading">{{ $t('กำลังโหลดข้อมูล...') }}</span>
      <span v-else-if="loadFailed">{{ $t('Unable to load options') }}</span>
      <span v-else-if="currentSearchQuery">{{ notFoundLabel }} "{{ currentSearchQuery }}"</span>
      <span v-else>{{ emptyLabel }}</span>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import reportService from '@/service/reportService'

// Staging's picker UI, backed by the same authorization as HDG bill reports.
const REPORT_SOURCE = {
  service: reportService,
  endpoint: 'poy/agents',
  notFoundLabel: 'ไม่พบตัวแทน',
  emptyLabel: 'ไม่มีข้อมูลตัวแทน',
  searchPlaceholder: 'พิมพ์ username เพื่อค้นหา...',
  showPrefix: false,
  markSelf: true,
}

export default {
  name: 'PoyAgentSelector',
  components: {
    vSelect,
  },
  props: {
    inputId: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: null,
    },
    // When true (default) the first option is auto-selected on mount. Pages that
    // pick a target to act on (e.g. add/remove credit) should pass false so the
    // field starts empty and nothing is selected by accident.
    autoSelectDefault: {
      type: Boolean,
      default: true,
    },
    // When true, selecting the current agent emits the option object instead of
    // using null as an implicit "self" value. Useful on screens where the target
    // must always be explicit to avoid editing the wrong agent.
    emitSelfAsOption: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      agentOptions: [],
      currentPage: 1,
      hasMoreAgents: false,
      loadFailed: false,
      loadingMore: false,
      initialLoading: true,
      currentSearchQuery: '',
      observer: null,
      requestId: 0,
      skipNextClear: false,
    }
  },
  computed: {
    displayOptions() {
      // Ensure selected value is always in the list
      if (this.value && this.value.code) {
        const isInList = this.agentOptions.some(opt => opt.code === this.value.code)
        if (!isInList) {
          // Selected value not in current list - add it to the top
          return [this.value, ...this.agentOptions]
        }
      }
      return this.agentOptions
    },
    sourceConfig() {
      return REPORT_SOURCE
    },
    resolvedPlaceholder() {
      return this.placeholder || this.$t(this.sourceConfig.searchPlaceholder)
    },
    notFoundLabel() {
      return this.$t(this.sourceConfig.notFoundLabel)
    },
    emptyLabel() {
      return this.$t(this.sourceConfig.emptyLabel)
    },
  },
  async mounted() {
    await this.loadDefaultAgent(this.autoSelectDefault)
  },
  beforeDestroy() {
    this.requestId += 1
    this.cleanup()
  },
  methods: {
    cleanup() {
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    mapAgentToOption(agent) {
      const withPrefix = this.sourceConfig.showPrefix && agent.prefix
      const { userData } = this.$store.state.app
      const currentAgentIds = [userData?.agent, userData?.username].filter(Boolean)
      return {
        label: withPrefix ? `${agent.username} (${agent.prefix})` : agent.username,
        code: agent.username,
        username: agent.username,
        prefix: agent.prefix,
        isSelf: !!this.sourceConfig.markSelf && currentAgentIds.includes(agent.username),
      }
    },

    async loadDefaultAgent(shouldAutoSelect = false) {
      this.requestId += 1
      const currentRequestId = this.requestId
      this.loadingMore = false
      this.initialLoading = true
      this.loadFailed = false
      this.hasMoreAgents = false
      try {
        const res = await this.sourceConfig.service.getAuthen(`${this.sourceConfig.endpoint}?page=1&limit=50`)
        if (currentRequestId !== this.requestId) return
        if (res.status !== 200 || !Array.isArray(res.data?.data)) throw new Error('Unable to load options')
        if (res.status === 200 && res.data?.data) {
          if (res.data.data.length > 0) {
            const firstAgent = this.mapAgentToOption(res.data.data[0])
            this.agentOptions = res.data.data.map(this.mapAgentToOption)
            this.currentPage = 1
            this.hasMoreAgents = this.agentOptions.length < res.data.total

            // Only emit default agent on initial load, not when clearing search
            if (shouldAutoSelect) {
              // If parent already has a value (e.g. restored from localStorage), keep it
              if (this.value && this.value.code) {
                const normalizedValue = this.agentOptions.find(option => option.code === this.value.code) || this.value
                this.$emit('input', normalizedValue)
                this.$emit('change', normalizedValue)
              } else {
                // Check if first agent is current user
                const currentUsername = this.$store.state.app.userData?.username
                const isCurrentUser = currentUsername && firstAgent.code === currentUsername

                if (isCurrentUser && !this.emitSelfAsOption) {
                  this.$emit('input', null)
                  this.$emit('change', null)
                } else {
                  this.$emit('input', firstAgent)
                  this.$emit('change', firstAgent)
                }
              }
            }
          } else {
            // No agents available
            this.agentOptions = []
            this.hasMoreAgents = false
          }
        }
      } catch (error) {
        if (currentRequestId === this.requestId) {
          this.loadFailed = true
          this.agentOptions = []
          this.hasMoreAgents = false
          this.$emit('error', error)
        }
      } finally {
        if (currentRequestId === this.requestId) this.initialLoading = false
      }
    },
    async searchAgents(query, loading) {
      const newQuery = query || ''

      if (!newQuery || newQuery.length < 2) {
        loading(false)
        // Skip if this clear is from selecting an item
        if (this.skipNextClear) {
          this.skipNextClear = false
          return
        }

        // Clear search - reload default list
        this.currentSearchQuery = ''
        await this.loadDefaultAgent(false)
        return
      }

      this.currentSearchQuery = newQuery
      this.initialLoading = false
      this.loadingMore = false
      this.loadFailed = false
      this.hasMoreAgents = false

      this.requestId += 1
      const currentRequestId = this.requestId

      loading(true)
      try {
        const res = await this.sourceConfig.service.getAuthen(`${this.sourceConfig.endpoint}?search=${encodeURIComponent(newQuery)}&page=1&limit=50`)

        // Check if this is still the latest request
        if (currentRequestId !== this.requestId) {
          return
        }

        if (res.status !== 200 || !Array.isArray(res.data?.data)) throw new Error('Unable to load options')

        if (res.status === 200 && res.data?.data) {
          this.agentOptions = res.data.data.map(this.mapAgentToOption)
          this.currentPage = 1
          this.hasMoreAgents = this.agentOptions.length < res.data.total
        }
      } catch (error) {
        if (currentRequestId === this.requestId) {
          this.loadFailed = true
          this.agentOptions = []
          this.hasMoreAgents = false
        }
      } finally {
        if (currentRequestId === this.requestId) {
          loading(false)
        }
      }
    },
    async loadMoreAgents() {
      if (this.loadingMore || !this.hasMoreAgents) return

      this.loadingMore = true
      this.loadFailed = false
      const currentRequestId = this.requestId
      const nextPage = this.currentPage + 1

      // Save scroll position
      const dropdownList = document.querySelector('.vs__dropdown-menu')
      const scrollTop = dropdownList ? dropdownList.scrollTop : 0

      try {
        const searchParam = this.currentSearchQuery ? `&search=${encodeURIComponent(this.currentSearchQuery)}` : ''
        const res = await this.sourceConfig.service.getAuthen(`${this.sourceConfig.endpoint}?page=${nextPage}&limit=50${searchParam}`)
        if (currentRequestId !== this.requestId) return
        if (res.status !== 200 || !Array.isArray(res.data?.data)) throw new Error('Unable to load options')

        if (res.status === 200 && res.data?.data) {
          const newAgents = res.data.data.map(this.mapAgentToOption)
          this.agentOptions = [...this.agentOptions, ...newAgents]
          this.currentPage = nextPage
          this.hasMoreAgents = this.agentOptions.length < res.data.total

          // Restore scroll position
          this.$nextTick(() => {
            if (dropdownList) {
              dropdownList.scrollTop = scrollTop
            }
          })
        }
      } catch (error) {
        if (currentRequestId === this.requestId) {
          this.loadFailed = true
          this.hasMoreAgents = false
        }
      } finally {
        if (currentRequestId === this.requestId) this.loadingMore = false
      }
    },
    onDropdownOpen() {
      this.$nextTick(() => {
        this.setupIntersectionObserver()
      })
    },
    setupIntersectionObserver() {
      if (!this.$refs.loadMoreTrigger) return

      // Disconnect old observer if exists
      if (this.observer) {
        this.observer.disconnect()
      }

      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && this.hasMoreAgents && !this.loadingMore) {
          this.loadMoreAgents()
        }
      }, {
        threshold: 0.5,
      })

      this.observer.observe(this.$refs.loadMoreTrigger)
    },
    async handleInput(value) {
      // When selecting an item, set flag to skip the auto-clear reload
      if (value !== null) {
        this.skipNextClear = true
      } else {
        // Clear the filter immediately; refreshing options must not delay it.
        this.currentSearchQuery = ''
      }

      this.$emit('input', value)
      this.$emit('change', value)
      if (value === null) await this.loadDefaultAgent(false)
    },
  },
}
</script>

<style scoped>
.agent-username {
  font-weight: normal;
}

.agent-prefix {
  font-size: 0.85em;
  opacity: 0.6;
  margin-left: 4px;
}

.agent-self-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 7px;
  border-radius: 999px;
  color: var(--hdg-brand-primary-emphasis, #c8102e);
  background: var(--hdg-brand-hover-bg, rgba(200, 16, 46, 0.08));
  font-size: 0.72em;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

/* Keep the self marker readable when vue-select paints the selected row red. */
>>> .vs__dropdown-option--selected .agent-self-badge {
  color: var(--hdg-brand-primary-emphasis, #c8102e);
  background: #fff;
}

/* Vuexy sm: $input-height-sm=2.142rem, $font-size-sm=0.857rem, $line-height-sm=1 */
.vs-sm >>> .vs__dropdown-toggle {
  height: 2.142rem;
  min-height: 2.142rem;
  padding: 0 6px;
  border-radius: 0.357rem;
}
.vs-sm >>> .vs__search,
.vs-sm >>> .vs__search::placeholder,
.vs-sm >>> .vs__selected {
  font-size: 0.857rem;
  line-height: 1;
  margin: 0;
  padding: 0;
}
.vs-sm >>> .vs__actions {
  padding: 0 4px 0 2px;
}
</style>
